import { AsyncPipe, KeyValuePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MdIconComponent } from '@inditex/mduikit';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, combineLatest, filter, map } from 'rxjs';
import SwiperCore, { Scrollbar, Swiper } from 'swiper';

import { ProductFilterService } from '../../../../services/product-filter.service';
import { ProductFilterItem } from '../../models/product-filter.models';

import { CorePlatformService } from '@core/infrastructure/platform/platform.service';
import { getCurrencyInfo } from '@core/infrastructure/state/core.selectors';
import { formatPriceWithCurrencySymbol } from '@frontend/common/currency';
import { Resolutions } from '@layout/domain/models/layout.models';
import { getResolution } from '@layout/infrastructure/state/layout.selectors';
import { CategoryFilterState, getFilters } from '@pages/category/public_api';
import { SetFilterItemProduct } from '@pages/category/store/category-page.actions';

SwiperCore.use([Scrollbar]);
@Component({
    selector: 'category-filter-badge',
    templateUrl: './category-filter-badge.component.html',
    standalone: true,
    imports: [
        AsyncPipe,
        KeyValuePipe,
        MdIconComponent,
        TranslateModule,
    ],
    providers: [
        ProductFilterService,
    ],
})
export class CategoryFilterBadgeComponent implements OnInit {
    private readonly getCurrencyInfo$ = this.store.select(getCurrencyInfo);

    private readonly PRICE_FILTER = 'maxPriceFilter';

    public isMobile$: Observable<boolean> = this.store.select(getResolution)
        .pipe(map((resolution) => resolution === Resolutions.MOBILE));

    public isBrowser = this.platformService.isBrowser();

    public readonly uuid = Date.now().toString();

    public readonly swiperFiltersClass: string = `swiper-${this.uuid}`;

    public swiperComponent?: Swiper;

    public getFilters$: Observable<CategoryFilterState> = this.store.select(getFilters);

    public filtersProduct$: Observable<Map<string, ProductFilterItem>> = combineLatest([
        this.getFilters$,
        this.getCurrencyInfo$,
        this.isMobile$,
    ]).pipe(
        filter(([data]) => !!data?.filters),
        map(([data, currencyInfo, isMobile]) => {
            let selectedFilters = ProductFilterService.getFilterApplied(data.filters);
            const mapFilters = new Map<string, ProductFilterItem>();
            let priceFilters = selectedFilters.filter(item => item.filterGroup === this.PRICE_FILTER);

            if (priceFilters.length > 0) {
                priceFilters = priceFilters.sort((a, b) => +a.price - +b.price);

                const parsedFilter = selectedFilters.filter(item => item.filterGroup !== this.PRICE_FILTER);
                const lowerPrice = formatPriceWithCurrencySymbol(+priceFilters[0].price, currencyInfo, 'down');

                const priceFilter = priceFilters.pop();
                const upperPrice = formatPriceWithCurrencySymbol(+priceFilter.price, currencyInfo, 'up');
                priceFilter.value = `${lowerPrice} - ${upperPrice}`;
                parsedFilter.push(priceFilter);
                selectedFilters = parsedFilter;
            }

            // biome-ignore lint/complexity/noForEach: <explanation>
            selectedFilters.forEach(selectedFilter => {
                mapFilters.set(
                    this.productFilterService.getFilterDescription(selectedFilter, selectedFilter.filterGroup)?.toString(),
                    selectedFilter
                );
            });

            if (this.isBrowser && !isMobile) {
                setTimeout(() => {
                    this.swiperComponent?.update();
                }, 10);
            }

            return mapFilters;
        })
    );

    constructor(
        public productFilterService: ProductFilterService,
        private readonly store: Store,
        private readonly platformService: CorePlatformService,
    ) { }

    public ngOnInit(): void {
        if (this.isBrowser) {
            setTimeout(() => {
                this.swiperComponent = new Swiper(`.${this.swiperFiltersClass}`, {
                    freeMode: true,
                    slidesPerView: 'auto',
                    initialSlide: 0,
                    breakpoints: {
                        768: {
                            freeMode: false,
                            slidesPerView: 'auto',
                            initialSlide: 0,
                        },
                    },
                });
                return this.swiperComponent;
            }, 10);
        }
    }

    public removeFilter(filters: CategoryFilterState, filterToRemove: ProductFilterItem): void {
        const itemToRemove = filters.filters[filterToRemove.filterGroup].map((item) => {
            return {
                ...item,
                isChecked: item.name === filterToRemove.name ? false : item.isChecked,
            };
        });
        this.store.dispatch(SetFilterItemProduct({ filterName: filterToRemove.filterGroup, filterValues: itemToRemove }));
    }
}
